import type { NewName } from "./types";
import { newNameSchema } from "./types";

export type ParsedTsvRow = NewName & { readonly line: number };

export const normalizeName = (value: string): string =>
  value.trim().toLocaleLowerCase().replace(/\s+/gu, " ");

export const levenshteinDistance = (left: string, right: string): number => {
  const previous = Array.from(
    { length: right.length + 1 },
    (_, index) => index,
  );

  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    let diagonal = previous[0] ?? 0;
    previous[0] = leftIndex;
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const previousAbove = previous[rightIndex] ?? 0;
      const cost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1;
      previous[rightIndex] = Math.min(
        (previous[rightIndex - 1] ?? 0) + 1,
        previousAbove + 1,
        diagonal + cost,
      );
      diagonal = previousAbove;
    }
  }
  return previous[right.length] ?? 0;
};

export const findSimilarNames = (
  candidate: string,
  existing: readonly string[],
): readonly string[] => {
  const normalizedCandidate = normalizeName(candidate);
  return existing.filter((name) => {
    const normalized = normalizeName(name);
    return (
      normalized !== normalizedCandidate &&
      levenshteinDistance(normalizedCandidate, normalized) <= 1
    );
  });
};

export type ParsedTsvResult = {
  readonly rows: readonly ParsedTsvRow[];
  readonly errors: readonly string[];
};

export const parseTsv = (value: string): ParsedTsvResult => {
  const rows: ParsedTsvRow[] = [];
  const errors: string[] = [];

  value.split(/\r?\n/u).forEach((line, index) => {
    if (line.trim() === "") {
      return;
    }
    const fields = line.split("\t");
    const candidate = {
      name: fields[0]?.trim() ?? "",
      gender: fields[1]?.trim() || "neutral",
      notes: fields.slice(2).join("\t").trim().replace(/\\n/gu, "\n"),
    };
    const result = newNameSchema.safeParse(candidate);
    if (result.success) {
      rows.push({ ...result.data, line: index + 1 });
    } else {
      const firstIssue = result.error.issues[0];
      const message =
        firstIssue === undefined ? "invalid row" : firstIssue.message;
      errors.push(`Line ${index + 1}: ${message}`);
    }
  });

  return { rows, errors };
};
